/**
 * @file Hook to hide the tab navigator's header while a nested player layout
 * is focused, restoring it on blur.
 */
import { useFocusEffect, useNavigation } from "expo-router";
import { useCallback } from "react";

import useMobileScreen from "@/hooks/useMobileScreen";

/** Minimal navigator shape needed to walk up to the tab navigator. */
interface NavLike {
  readonly getParent?: () => NavLike | undefined;
  readonly getState?: () => { readonly type?: string } | undefined;
  readonly setOptions?: (options: { headerShown: boolean }) => void;
}

/**
 * Walk up the navigator chain to the bottom-tab navigator — the one that owns
 * the tab header (TabViewHeader) — regardless of how deeply the caller is
 * nested. Returns undefined when there is no tab-navigator ancestor.
 *
 * A blind `getParent()` is wrong here: for `search/players` (a grandchild of
 * the tab navigator) it lands on the tab navigator, but for the top-level
 * `players` stack (a direct child) it lands on the ROOT stack, whose "(tabs)"
 * screen header would then get switched on (SE-5452).
 * @param nav - Navigation object to start walking from
 * @returns The tab navigator ancestor, or undefined
 */
const findTabNavigator = (nav: NavLike | undefined): NavLike | undefined => {
  const parent = nav?.getParent?.();
  if (!parent) return undefined;
  if (parent.getState?.()?.type === "tab") return parent;
  return findTabNavigator(parent);
};

/**
 * Hides the tab navigator's header while this nested layout is focused and
 * restores it to the screen-size-appropriate default (`headerShown:
 * isSmallScreen`) on blur.
 * @remarks Gated on focus (not mount) so the header is restored on EVERY exit
 * path — including in-app `router.navigate(...)` back buttons that don't
 * unmount the nested stack (that non-restore was the original Search-header bug
 * on breadcrumb back). It targets the tab navigator explicitly rather than a
 * blind `getParent()`, so the root "(tabs)" route-group Stack header is never
 * switched on from the top-level players stack (scouting/home).
 */
export function useHideParentHeader(): void {
  const navigation = useNavigation() as NavLike;
  const isSmallScreen = useMobileScreen();

  useFocusEffect(
    useCallback(() => {
      const tabNavigator = findTabNavigator(navigation);
      tabNavigator?.setOptions?.({ headerShown: false });

      return () => {
        tabNavigator?.setOptions?.({ headerShown: isSmallScreen });
      };
    }, [navigation, isSmallScreen])
  );
}
